import type { LogicReferenceKind, LogicReferenceNode, TradeLogicJsonObject } from './types';

export const referenceKindOptions: { value: LogicReferenceKind; label: string }[] = [
    { value: 'scorecard', label: 'Scorecard' },
    { value: 'screener', label: 'Screener' },
    { value: 'definition', label: 'Definition' },
    { value: 'version', label: 'Version' },
    { value: 'candidate', label: 'Candidate' },
    { value: 'block', label: 'Reusable block' }
];

export interface ReferenceNodeInputState {
    parametersJson: string;
    referenceId: string;
    referenceKind: LogicReferenceKind;
    version: string;
}

export const buildReferenceNodeInputState = (node?: LogicReferenceNode | null): ReferenceNodeInputState => ({
    parametersJson: JSON.stringify(node?.parameters ?? {}, null, 2),
    referenceId: node ? String(node.referenceId) : '',
    referenceKind: node?.referenceKind ?? 'definition',
    version: node?.version === null || node?.version === undefined ? '' : String(node.version)
});

export const parseReferenceNodeInputState = (
    state: ReferenceNodeInputState
): Pick<LogicReferenceNode, 'parameters' | 'referenceId' | 'referenceKind' | 'version'> => ({
    parameters: parseReferenceParametersJson(state.parametersJson),
    referenceId: parseReferenceId(state.referenceId),
    referenceKind: state.referenceKind,
    version: parseOptionalReferenceVersion(state.version)
});

export const buildReferenceNodeLabel = (referenceKind: LogicReferenceKind, referenceId: string | number): string =>
    `${referenceKind}: ${String(referenceId)}`;

export const isAutoReferenceNodeLabel = (node: LogicReferenceNode): boolean =>
    node.label === buildReferenceNodeLabel(node.referenceKind, node.referenceId) ||
    referenceKindOptions.some((option) => node.label?.startsWith(`${option.value}: `));

const parseReferenceId = (rawValue: string): string | number => {
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
        throw new Error('Reference ID is required');
    }

    if (/^\d+$/.test(trimmedValue)) {
        return Number(trimmedValue);
    }

    return trimmedValue;
};

const parseOptionalReferenceVersion = (rawValue: string): number | null => {
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) return null;

    const parsed = Number(trimmedValue);

    if (!Number.isInteger(parsed) || parsed < 1) {
        throw new Error('Reference version must be a positive integer');
    }

    return parsed;
};

const parseReferenceParametersJson = (rawValue: string): TradeLogicJsonObject => {
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) return {};

    let parsed: unknown;

    try {
        parsed = JSON.parse(trimmedValue);
    } catch {
        throw new Error('Reference parameters must be valid JSON');
    }

    if (!isTradeLogicJsonObject(parsed)) {
        throw new Error('Reference parameters must be a JSON object');
    }

    return parsed;
};

const isTradeLogicJsonObject = (value: unknown): value is TradeLogicJsonObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
